import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { List, Paper } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import useTickets from "../../hooks/useTickets";

import SocketTicketsList from "../../context/Socket/Listeners/SocketTicketsList";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		borderRadius: "20px",
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderBottom:"hidden",
	},

	ticketsList: {
		borderRadius:"20px",
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		borderRadius:"20px",
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		borderRadius:"20px",
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		borderRadius:"20px",
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		borderRadius:"20px",
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		borderRadius:"20px",
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));


// 	********************************
// 	** Global Functions - Sorting **
// 	********************************
const sortGroupTickets = (sortingMethod) => {
	return (ticketA, ticketB) => {
		const currentDate = new Date();
		const timeExistenceTicketA = currentDate - new Date(ticketA.createdAt);
		const timeExistenceTicketB = currentDate - new Date(ticketB.createdAt);

		return sortingMethod === "asc"
			? timeExistenceTicketB - timeExistenceTicketA
			: timeExistenceTicketA - timeExistenceTicketB;
	};
};

const sortPendingTickets = (sortingMethod) => {
	return (ticketA, ticketB) => {
		const currentDate = new Date();
		const timeAwaitingTicketA = currentDate - new Date(ticketA.createdAt);
		const timeAwaitingTicketB = currentDate - new Date(ticketB.createdAt);

		return sortingMethod === "asc"
			? timeAwaitingTicketB - timeAwaitingTicketA
			: timeAwaitingTicketA - timeAwaitingTicketB;
	};
};

const sortOpenTickets = (sortingMethod) => {
	return (ticketA, ticketB) => {
		const currentDate = new Date();
		const timeInAttendanceTicketA = currentDate - new Date(ticketA.withUserAt);
		const timeInAttendanceTicketB = currentDate - new Date(ticketB.withUserAt);

		return sortingMethod === "asc"
			? timeInAttendanceTicketB - timeInAttendanceTicketA
			: timeInAttendanceTicketA - timeInAttendanceTicketB;
	};
};

const sortClosedTickets = (sortingMethod) => {
	return (ticketA, ticketB) => {
		const timeFinalizedTicketA = new Date(ticketA.isClosedAt) - new Date(ticketA.withUserAt);
		const timeFinalizedTicketB = new Date(ticketB.isClosedAt) - new Date(ticketB.withUserAt);

		return sortingMethod === "asc"
			? timeFinalizedTicketB - timeFinalizedTicketA
			: timeFinalizedTicketA - timeFinalizedTicketB;
	};
};

const sortDynamicTickets = (ticketA, ticketB) => {
	const updatedDateTicketA = new Date(ticketA.updatedAt);
	const updatedDateTicketB = new Date(ticketB.updatedAt);
	return updatedDateTicketB - updatedDateTicketA;
};

// 	**********************************
// 	** Global Functions - Filtering **
// 	**********************************
const filterGroupTickets = (userId) => {
	return (ticket) => {
		if (ticket.type === 0) return true;
		if (ticket.userId === userId) return true;
		return ticket.ticketUsers.some(ticketUser => ticketUser.userId === userId);
	};
};

//	**************
// 	** Reducers **
// 	**************
const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);

			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) { state.push(state.splice(ticketIndex, 1)[0]); }
			}
			else { state.push(ticket); }
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
			state[ticketIndex].isMeMentioned = false;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		}

		else {
			state.push(ticket);

			const { sortingMethod } = action.misc;
			if (ticket.status === "group") state.sort(sortGroupTickets(sortingMethod));
			else if (ticket.status === "pending") state.sort(sortPendingTickets(sortingMethod));
			else if (ticket.status === "open") state.sort(sortOpenTickets(sortingMethod));
			else if (ticket.status === "closed") state.sort(sortClosedTickets(sortingMethod));
		}

		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) { state.splice(ticketIndex, 1); }
		return [...state];
	}
	
	if (action.type === "RESET") { return []; }
};

// 	***************
// 	** Component **
// 	***************
const TicketsList = ({ 
	filter, isGroup, resetAudioPlayer, setTabOpen, showAll,
	status, style, updateCount, selectedUserIds, selectedConnectionIds
}) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);

	const [pageNumber, setPageNumber] = useState(1);

	const [prioritizeUserSetting, setPrioritizeUserSetting] = useState("disabled");
	const [prioritizeUserTimeSetting, setPrioritizeUserTime] = useState(2);
	const [ticketsListSortMethodSetting, setTicketsListSortMethodSetting] = useState("asc");
	const [ticketsDynamicSortingSetting, setTicketsDynamicSortingSetting] = useState("disabled");

	const queueIds = user.queues.map(queue => queue.id) || [];

	const params = {
		pageNumber,
		status,
		showAll,
		isGroup,
		filter: filter || "",
		queueIds: JSON.stringify(queueIds)
	};

	if (["open", "closed"].includes(status)) {
		params.selectedUserIds = selectedUserIds !== undefined ? selectedUserIds : [];
	}

	if (["open", "closed", "pending"].includes(status)) {
		params.selectedConnectionIds = selectedConnectionIds !== undefined ? selectedConnectionIds : [];
	}

	const { tickets, hasMore, loading } = useTickets(params);
	const { getSetting, isLoading } = useGlobalRegisters();



	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, dispatch, showAll, filter, selectedUserIds, selectedConnectionIds]);

	useEffect(() => {
		if (!status) return;
		dispatch({ type: "LOAD_TICKETS", payload: tickets });
	}, [tickets, status]);

	useEffect(() => {
		if (status === "pending") { 
			const pendingTickets = ticketsList.filter(ticket => ticket.status === "pending");
			const sumUnreadMessages = pendingTickets.reduce((total, current) => current.unreadMessages > 0 ? total + 1 : total, 0);
			document.title = sumUnreadMessages > 0  ? `(${sumUnreadMessages}) BestZap` : "BestZap";
		}
	});

	useEffect(() => {
		if (isLoading) return;

		setPrioritizeUserSetting(getSetting("prioritizeUser"));
		setTicketsListSortMethodSetting(getSetting("ticketsListSortMethod"));
		setTicketsDynamicSortingSetting(getSetting("ticketsDynamicSorting"));

		const prioritizeUserTimeResponse = getSetting("prioritizeUserTime");
		if (prioritizeUserTimeResponse === "infinity") setPrioritizeUserTime(prioritizeUserTimeResponse);
		else setPrioritizeUserTime(parseInt(prioritizeUserTimeResponse, 10));
	}, [getSetting, isLoading]);

	useEffect(() => {
		if (typeof updateCount !== "function") return;

		//
		// ***---- Groups ----***
		//
		if (status === "group") {
			const count = ticketsList.filter(ticket => {
				if (ticket.type === 0) return true;
				if (ticket.userId === user.id) return true;
				return ticket.ticketUsers.some(ticketUser => ticketUser.userId === user.id)
			}).length;

			updateCount(count);

			return;
		}

		//
		// ***---- Open and Closed Tickets ----***
		//
		if (!["group", "pending"].includes(status)) {
			updateCount(ticketsList.length);
			return;
		}

		//
		//	***---- Pending Tickets ----***
		//
		if (status === "pending") {
			if (prioritizeUserSetting === "enabled") {
				let pendingCounter = 0;

				let whatsappTicketsCount = 0;
				const noteTicketsCount = ticketsList.filter(ticket => ticket.type === 1).length;
				const botTicketsCount = ticketsList.filter(ticket => ticket.type === 2).length;
				const internalChatTicketsCount = ticketsList.filter(ticket => ticket.type === 3).length;

				ticketsList.filter(ticket => ticket.type === 0).forEach(ticket => {
					let userExists;
	
					try {
						userExists = ticket?.contact?.users.length === 0 
							? true
							: ticket?.contact?.users.some(contactUser => contactUser.id === user.id);
					} catch (error) {
						userExists = [];
					}

					pendingCounter = !(
							(prioritizeUserTimeSetting !== "infinity" && prioritizeUserTimeSetting * 60 * 1000 < (new Date() - new Date(ticket.createdAt)))
							|| (ticket?.type === 1 || ticket?.type === 2 || (ticket?.type === 0 && userExists))
						)
							? pendingCounter + 1
							: pendingCounter;

					if (ticket.whatsapp && !ticket.whatsapp.waitForBotInteraction) whatsappTicketsCount += 1;
					else if (user.profile === "admin") whatsappTicketsCount += 1;
					else if (ticket.contact.isBot || ticket.isBotInteractionFinished) whatsappTicketsCount += 1;
				});

				updateCount(noteTicketsCount + botTicketsCount + internalChatTicketsCount + whatsappTicketsCount - pendingCounter);
			}
			
			else {
				let whatsappTicketsCount = 0;
				const noteTicketsCount = ticketsList.filter(ticket => ticket.type === 1).length;
				const botTicketsCount = ticketsList.filter(ticket => ticket.type === 2).length;
				const internalChatTicketsCount = ticketsList.filter(ticket => ticket.type === 3).length;

				ticketsList.filter(ticket => ticket.type === 0).forEach(ticket => {
					if (ticket.whatsapp && !ticket.whatsapp.waitForBotInteraction) whatsappTicketsCount += 1;
					else if (user.profile === "admin") whatsappTicketsCount += 1;
					else if (ticket.contact.isBot || ticket.isBotInteractionFinished) whatsappTicketsCount += 1;
				});

				updateCount(noteTicketsCount + botTicketsCount + internalChatTicketsCount + whatsappTicketsCount);
			}
		}

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);



	// 	***************
	// 	** Functions **
	// 	***************
	const handleScroll = (event) => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			event.currentTarget.scrollTop = scrollTop - 100;
			setPageNumber(prevState => prevState + 1);
		}
	};

	

	// 	************
	//  ** Return **
	// 	************
	return (
    <Paper elevation={0} className={classes.ticketsListWrapper} style={style}>
			<SocketTicketsList
				dispatch={dispatch}
				filter={filter}
				selectedQueueIds={queueIds}
				selectedUserIds={selectedUserIds}
				selectedConnectionIds={selectedConnectionIds}
				status={status}
				ticketsListSortMethodSetting={ticketsListSortMethodSetting}
			/>

			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>{i18n.t("ticketsList.noTicketsTitle")}</span>
							<p className={classes.noTicketsText}>{i18n.t("ticketsList.noTicketsMessage")}</p>
						</div>
					) : (
						<>
							{/* 
								******************************
								** Dynamic Sorting: Enabled **
								******************************
								//
								//	- all tickets are sorted by the most recent message;
								//
							*/}
							{status === "group"
								&& ticketsDynamicSortingSetting === "enabled"
								&& ticketsList.filter(filterGroupTickets(user.id)).sort(sortDynamicTickets).map(ticket => (
									<TicketListItem
										resetAudioPLayer={resetAudioPlayer}
										ticket={ticket}
										ticketsList={ticketsList}
										setTabOpen={setTabOpen}
									/>
								))
							}

							{["closed", "open"].includes(status)
								&& ticketsDynamicSortingSetting === "enabled"
								&& ticketsList.sort(sortDynamicTickets).map(ticket => (
									<TicketListItem
										resetAudioPlayer={resetAudioPlayer}
										ticket={ticket}
										ticketsList={ticketsList}
										setTabOpen={setTabOpen}
									/>
								))
							}

							{status === "pending"
								&& ticketsDynamicSortingSetting === "enabled"
								&& ticketsList.sort(sortDynamicTickets).map(ticket => (
									<>
										{/* 
											***---- Notes and Bots ----***
										*/}
										{ticket.type !== 0 && (
											<TicketListItem
												resetAudioPlayer={resetAudioPlayer}
												ticket={ticket}
												ticketsList={ticketsList}
												setTabOpen={setTabOpen}
											/>
										)}



										{/* 
											***---- WhatsApps ----***

											- Tickets are always visible when:
												\ whatsapp is not enabled to wait for bot interaction;

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& !ticket.whatsapp.waitForBotInteraction
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}



										{/* 
											***---- WhatsApps ----***

											- Tickets are visible when:
												\ whatsapp is enabled for bot interaction;
												\ AND user profile is admin;

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& ticket.whatsapp.waitForBotInteraction
											&& user.profile === "admin"
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}



										{/* 
											***---- WhatsApps ----***

											- Tickets are visible when:
												\ whatsapp is enabled for bot interaction;
												\ AND user profile is not admin;
												\ AND (contact is bot (set to don't receive automatic messages);
												\ OR bot interaction on ticket is finished);

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& ticket.whatsapp.waitForBotInteraction
											&& user.profile === "user"
											&& (ticket.contact.isBot || ticket.isBotInteractionFinished)
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}
									</>
								))
							}

							{/* 
								*******************************
								** Dynamic Sorting: Disabled **
								*******************************
								//
								//	- group tickets: are sorted by the difference of 'currentDate - ticket.createdAt';
								// 	- open tickets: are sorted by the difference of 'current - ticket.withUserAt';
								//	- closed tickets: are sorted by the difference of 'ticket.isClosedAt - ticket.withUserAt';
								//	- pending tickets: are sorted by the difference of 'currentDate - ticket.createdAt';
								//
							*/}
							{status === "group"
								&& ticketsDynamicSortingSetting === "disabled"
								&& ticketsList.filter(filterGroupTickets(user.id)).sort(sortGroupTickets(ticketsListSortMethodSetting)).map(ticket => (
									<TicketListItem
										resetAudioPlayer={resetAudioPlayer}
										ticket={ticket}
										ticketsList={ticketsList}
										setTabOpen={setTabOpen}
									/>
								))
							}

							{status === "open"
								&& ticketsDynamicSortingSetting === "disabled"
								&& ticketsList.sort(sortOpenTickets(ticketsListSortMethodSetting)).map(ticket => (
									<TicketListItem
										resetAudioPlayer={resetAudioPlayer}
										ticket={ticket}
										ticketsList={ticketsList}
										setTabOpen={setTabOpen}
									/>
								))
							}

							{status === "closed"
								&& ticketsDynamicSortingSetting === "disabled"
								&& ticketsList.sort(sortClosedTickets(ticketsListSortMethodSetting)).map(ticket => (
									<TicketListItem
										resetAudioPlayer={resetAudioPlayer}
										ticket={ticket}
										ticketsList={ticketsList}
										setTabOpen={setTabOpen}
									/>
								))
							}

							{status === "pending"
								&& ticketsDynamicSortingSetting === "disabled"
								&& ticketsList.sort(sortPendingTickets(ticketsListSortMethodSetting)).map(ticket => (
									<>
										{/* 
											***---- Notes and Bots ----***
										*/}
										{ticket.type !== 0 && (
											<TicketListItem
												resetAudioPlayer={resetAudioPlayer}
												ticket={ticket}
												ticketsList={ticketsList}
												setTabOpen={setTabOpen}
											/>
										)}



										{/* 
											***---- WhatsApps ----***

											- Tickets are always visible when:
												\ whatsapp is not enabled to wait for bot interaction;

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& !ticket.whatsapp.waitForBotInteraction
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}



										{/* 
											***---- WhatsApps ----***

											- Tickets are visible when:
												\ whatsapp is enabled for bot interaction;
												\ AND user profile is admin;

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& ticket.whatsapp.waitForBotInteraction
											&& user.profile === "admin"
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}



										{/* 
											***---- WhatsApps ----***

											- Tickets are visible when:
												\ whatsapp is enabled for bot interaction;
												\ AND user profile is not admin;
												\ AND (contact is bot (set to don't receive automatic messages);
												\ OR bot interaction on ticket is finished);

										*/}
										{ticket.type === 0
											&& ticket.whatsapp
											&& ticket.whatsapp.waitForBotInteraction
											&& user.profile === "user"
											&& (ticket.contact.isBot|| ticket.isBotInteractionFinished)
											&& (
												<TicketListItem
													resetAudioPlayer={resetAudioPlayer}
													ticket={ticket}
													ticketsList={ticketsList}
													setTabOpen={setTabOpen}
												/>
											)
										}
									</>
								))
							}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;